import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import axios from "axios";

/**
 * Inline "Book a viewing" modal. POSTs to /api/viewing-requests.
 * Keeps state locally — parent just controls open/close.
 */
export const ViewingModal = ({ open, onClose, listing }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    preferred_date: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      setError("Please share your name and email.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/viewing-requests`, {
        ...form,
        property_slug: listing.slug,
        property_name: `${listing.name} ${listing.unit}`,
      });
      setSubmitted(true);
    } catch (err) {
      setError("Something went wrong. Please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-center justify-center px-4 py-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          style={{ backgroundColor: "rgba(20,35,27,0.55)", backdropFilter: "blur(6px)" }}
          onClick={onClose}
          data-testid="viewing-modal"
        >
          <motion.div
            className="relative w-full max-w-[560px] overflow-hidden"
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            style={{
              borderRadius: 20,
              backgroundColor: "#F8F5EF",
              boxShadow: "0 30px 80px -20px rgba(20,35,27,0.55)",
            }}
          >
            <button
              type="button"
              onClick={onClose}
              className="absolute right-4 top-4 z-10"
              aria-label="Close"
              data-testid="viewing-modal-close"
              style={{ color: "#24382E" }}
            >
              <X size={22} strokeWidth={1.5} />
            </button>

            {submitted ? (
              <div className="px-8 py-14 text-center">
                <p
                  className="label-caps text-[11px]"
                  style={{ color: "#C89A4E" }}
                >
                  Sent
                </p>
                <h3
                  className="font-display mt-5"
                  style={{ fontSize: 34, color: "#201E1A", lineHeight: 1.05 }}
                  data-testid="viewing-success-title"
                >
                  We&rsquo;ll be in touch shortly.
                </h3>
                <p
                  className="mt-4 text-[15px] leading-[1.6]"
                  style={{ color: "rgba(32,30,26,0.72)" }}
                >
                  Someone from the ROAM team will reach out within one business
                  day to lock in a walkthrough of {listing.name} {listing.unit}.
                </p>
                <button
                  type="button"
                  onClick={onClose}
                  className="pill pill-pine mt-8 text-sm"
                  data-testid="viewing-success-close"
                >
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="px-8 py-10">
                <p
                  className="label-caps text-[11px]"
                  style={{ color: "#C89A4E" }}
                >
                  Book a viewing
                </p>
                <h3
                  className="font-display mt-4"
                  style={{ fontSize: 30, color: "#201E1A", lineHeight: 1.05 }}
                >
                  {listing.name}{" "}
                  <span style={{ color: "rgba(32,30,26,0.5)" }}>
                    {listing.unit}
                  </span>
                </h3>
                <p
                  className="mt-2 text-[14px]"
                  style={{ color: "rgba(32,30,26,0.65)" }}
                >
                  {listing.neighbourhood} · {listing.address}
                </p>

                <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Input
                    label="Your name"
                    value={form.name}
                    onChange={upd("name")}
                    required
                    testId="viewing-input-name"
                  />
                  <Input
                    label="Email"
                    type="email"
                    value={form.email}
                    onChange={upd("email")}
                    required
                    testId="viewing-input-email"
                  />
                  <Input
                    label="Phone"
                    value={form.phone}
                    onChange={upd("phone")}
                    testId="viewing-input-phone"
                  />
                  <Input
                    label="Preferred date"
                    type="date"
                    value={form.preferred_date}
                    onChange={upd("preferred_date")}
                    testId="viewing-input-date"
                  />
                </div>

                <label className="mt-4 block">
                  <span
                    className="label-caps text-[10px]"
                    style={{ color: "rgba(32,30,26,0.55)" }}
                  >
                    Anything we should know?
                  </span>
                  <textarea
                    value={form.message}
                    onChange={upd("message")}
                    rows={3}
                    className="mt-1 w-full resize-none border-b bg-transparent py-2 text-[15px] outline-none"
                    style={{
                      borderColor: "rgba(32,30,26,0.2)",
                      color: "#201E1A",
                    }}
                    data-testid="viewing-input-message"
                  />
                </label>

                {error && (
                  <p
                    className="mt-4 text-[13px]"
                    style={{ color: "#8B1A0F" }}
                    data-testid="viewing-error"
                  >
                    {error}
                  </p>
                )}

                <div className="mt-8 flex items-center gap-4">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="pill pill-pine text-sm disabled:opacity-60"
                    data-testid="viewing-submit"
                  >
                    {submitting ? "Sending…" : "Request viewing"}
                  </button>
                  <p
                    className="text-[12px]"
                    style={{ color: "rgba(32,30,26,0.55)" }}
                  >
                    Or call us at{" "}
                    <a href="tel:+15879001234" className="underline">
                      (587) 900-1234
                    </a>
                  </p>
                </div>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const Input = ({ label, testId, ...rest }) => (
  <label className="block">
    <span
      className="label-caps text-[10px]"
      style={{ color: "rgba(32,30,26,0.55)" }}
    >
      {label}
    </span>
    <input
      {...rest}
      data-testid={testId}
      className="mt-1 w-full border-b bg-transparent py-2 text-[15px] outline-none"
      style={{ borderColor: "rgba(32,30,26,0.2)", color: "#201E1A" }}
    />
  </label>
);

export default ViewingModal;
